import { css } from '@/styled-system/css';
import { cva } from '@/styled-system/css';
import { token } from '@/styled-system/tokens';

/**
 * FullscreenOverlay Backdrop - semi-transparent overlay
 */
export const backdrop = css({
  // Mobile: document flow — enables iOS Safari chrome retraction on scroll
  position: 'relative',
  background: 'bg.neutral',
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': {
    minHeight: '100dvh',
  },
  zIndex: 'zIndexOverLoader',
  // Desktop: fixed overlay with centering
  lg: {
    position: 'fixed',

    top: 0,

    left: 0,

    right: 0,

    bottom: 0,
    minHeight: 'initial',
    background: 'overlay',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

/**
 * FullscreenOverlay Content - main container
 */
export const content = cva({
  base: {
    position: 'relative',
    width: '100%',
    minHeight: '100vh',
    '@supports (min-height: 100dvh)': {
      minHeight: '100dvh',
    },
    background: 'bg.neutral',
    display: 'flex',
    flexDirection: 'column',
    _focusVisible: {
      outline: 'none',
    },
    lg: {
      width: 'auto',
      height: 'auto',
      minHeight: 'initial',
      // eslint-disable-next-line hotpads/no-magic-numbers -- modal-width cap that keeps HDP spoke content readable on desktop
      maxWidth: '900px',
      maxHeight: '90vh',
      overflow: 'hidden',
      borderRadius: 'md',
    },
  },
  variants: {
    variant: {
      standard: {
        // Standard variant has normal header + body + footer layout
      },
      media: {
        height: '100vh',
        overflow: 'hidden',
        '@supports (height: 100dvh)': {
          height: '100dvh',
        },
        lg: {
          width: '100%',
          height: '100%',
          maxWidth: 'none',
          maxHeight: 'none',

          borderRadius: 0,
        },
      },
    },
  },
});

/**
 * FullscreenOverlay Header - varies by variant and layout
 */
export const header = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '1200',
    paddingInline: 'tighter',
    paddingBlock: 'tighter',
    zIndex: 'zIndexParent',
  },
  variants: {
    layout: {
      sticky: {
        position: 'sticky',

        top: 0,
        background: 'bg.neutral',
      },
      floating: {
        position: 'absolute',

        top: 0,

        left: 0,

        right: 0,
        background: 'transparent',
        zIndex: 'zIndexOverParent',
      },
    },
    variant: {
      standard: {},
      media: {},
    },
  },
});

/**
 * Header column layout - three column grid
 */
export const headerColumns = css({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  width: '100%',
  gap: 'tighter',
});

export const headerColumn1 = css({
  justifySelf: 'start',
});

export const headerColumn2 = css({
  justifySelf: 'center',
});

export const headerColumn3 = css({
  justifySelf: 'end',
  display: 'flex',
  gap: 'tighter',
  alignItems: 'center',
});

export const headerTitle = css({
  fontSize: 'heading-xs',
  fontWeight: 'bold',
  color: 'text.neutral',
  textAlign: 'center',
});

export const headerSubtitle = css({
  fontSize: 'body-xs',
  color: 'text.subtle',
  textAlign: 'center',

  margin: 0,
  lineHeight: 'body-xs',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

/**
 * FullscreenOverlay Body - main content area
 */
export const body = cva({
  base: {
    flex: 1,
    position: 'relative',
    lg: {
      overflow: 'auto',
    },
  },
  variants: {
    padding: {
      standard: {
        paddingInline: 'default',
        paddingBlock: 'default',
      },
      'edge-to-edge': {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
      },
    },
    variant: {
      standard: {},
      media: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
      },
    },
  },
});

/**
 * FullscreenOverlay Footer - action buttons
 */
export const footer = cva({
  base: {
    position: 'sticky',

    bottom: 0,
    zIndex: 'zIndexParent',
    display: 'flex',
    alignItems: 'center',
    minHeight: '1800',
    paddingInline: 'default',
    paddingBlock: 'default',
    borderTopWidth: 'default',
    borderTopStyle: 'solid',
    borderTopColor: 'border.neutral',
    background: 'bg.neutral',
    lg: {
      position: 'static',
      zIndex: 'auto',
    },
  },
  variants: {
    variant: {
      buttons: {
        justifyContent: 'flex-end',
        gap: 'tighter',
      },
      'equal-width': {
        gap: 'tighter',
        '& > *': {
          flex: 1,
        },
      },
      text: {
        fontSize: 'body-sm',
        color: 'text.subtle',
      },
    },
    overlayVariant: {
      standard: {},
      media: {
        // Media variant has no footer
        display: 'none',
      },
    },
  },
});

/**
 * Media variant specific styles
 */
export const mediaContainer = css({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Inline hex — media variant wants a guaranteed pure-black backdrop
  // regardless of theme; no Constellation token resolves to a fixed black.
  background: 'black',
});

export const mediaContent = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const mediaOverlayInfo = css({
  position: 'absolute',

  bottom: 0,

  left: 0,

  right: 0,
  background: `linear-gradient(transparent, ${token.var('colors.transparent.black-75')})`,
  // text.onImpact.neutral maps to white in light theme and dark-blue in dark theme;
  // here we always want white on the gradient for contrast.
  color: 'white',
  padding: 'loose',
  zIndex: 'zIndexParent',
});

export const floatingActions = cva({
  base: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'default',
    zIndex: 'zIndexOverParent',
  },
  variants: {
    position: {
      left: {
        left: 'default',
      },
      right: {
        right: 'default',
      },
    },
  },
});

/**
 * Close button styling
 */
export const closeButton = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1000',
  height: '1000',
  borderRadius: '50%',
  background: 'transparent',
  borderWidth: 'default',
  borderStyle: 'solid',
  borderColor: 'border.neutral',
  color: 'text.neutral',
  cursor: 'pointer',
  transitionProperty: 'all',
  transitionDuration: '0.2s',

  '&:hover': {
    background: 'bg.softest',
    borderColor: 'border.subtle',
  },

  '&:focus-visible': {
    outlineWidth: 'thick',
    outlineStyle: 'solid',
    outlineColor: 'brand.primary',
    outlineOffset: '50',
  },
});

/**
 * Floating close button for media variant
 */
export const floatingCloseButton = css({
  position: 'absolute',
  top: 'default',
  left: 'default',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1000',
  height: '1000',
  borderRadius: '50%',
  background: 'transparent.white-80',
  border: 'none',
  // Floating close button on a translucent-white pill — needs a dark color
  // that doesn't theme-flip. Use semantic text.neutral which resolves to
  // dark blue in the default light theme.
  color: 'text.neutral',
  cursor: 'pointer',
  transitionProperty: 'all',
  transitionDuration: '0.2s',
  zIndex: 'zIndexOverParent',

  '&:hover': {
    background: 'white',
    transform: 'scale(1.05)',
  },

  '&:focus-visible': {
    outlineWidth: 'thick',
    outlineStyle: 'solid',
    outlineColor: 'brand.primary',
    outlineOffset: '50',
  },
});

/**
 * Visual treatment for close button when used inline in a floating media header (with actions).
 * Same look as floatingCloseButton but without absolute positioning.
 */
export const overlayBackButton = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tightest',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'text.neutral',
  fontSize: 'body-sm',
  fontWeight: 'bold',
  fontFamily: 'inherit',
  padding: 'tighter',
  borderRadius: 'sm',
  _hover: {
    background: 'bg.softest',
  },
  _focusVisible: {
    outlineWidth: 'thick',
    outlineStyle: 'solid',
    outlineColor: 'brand.primary',
    outlineOffset: '50',
  },
});

export const mediaCloseButtonInline = css({
  background: 'transparent.white-80',
  border: 'none',

  '&:hover': {
    background: 'white',
    transform: 'scale(1.05)',
  },
});
